import React from 'react';
import { CreditCard, PauseCircle } from 'lucide-react';
import { Button } from './ui/button';

const PAYMENT_LINK = 'https://square.link/u/J2GrZj2T';

/**
 * Account-inactive notice (Session 26 — private beta).
 *
 * Replaces the old hard "Activate for $19/month" payment banner. Shows ONLY
 * for providers whose trial/promo has actually expired (or who never paid),
 * stating account state — not gating the dashboard.
 *
 * Decision is driven by props from the calling page (which has access to
 * the provider profile + computed expiration state). Returns null when
 * provider is active / lifetime / paid.
 */
export default function AccountInactiveNotice({ provider, accountStatus }) {
  if (!provider) return null;
  if (provider.lifetimeAccess) return null;
  const sub = (provider.subscriptionStatus || '').toUpperCase();
  if (sub === 'PAID' || sub === 'ACTIVE' || sub === 'SUBSCRIBED') return null;

  // Compute expiry
  const endIso = provider.promoEndDate || provider.trialEndDate;
  let expired = false;
  if (endIso) {
    const end = new Date(endIso);
    if (!Number.isNaN(end.getTime()) && end.getTime() <= Date.now()) {
      expired = true;
    }
  }

  // Show only when the trial has actually ended OR account is non-ACTIVE.
  const status = (accountStatus || provider.accountStatus || '').toUpperCase();
  const shouldShow = expired || (status && status !== 'ACTIVE' && status !== 'PENDING');

  if (!shouldShow) return null;

  return (
    <div
      className="rounded-lg border border-slate-300 bg-slate-50 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid="account-inactive-notice"
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="bg-slate-200 p-2 rounded-full shrink-0">
          <PauseCircle className="h-5 w-5 text-slate-600" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900 text-sm sm:text-base">
            Your account is currently inactive
          </p>
          <p className="text-xs sm:text-sm text-slate-700 mt-0.5">
            Add payment to continue RanchWorks and start receiving bookings again.
          </p>
        </div>
      </div>
      <Button
        asChild
        size="sm"
        className="bg-slate-800 hover:bg-slate-900 text-white shrink-0"
        data-testid="account-inactive-pay-btn"
      >
        <a href={PAYMENT_LINK} target="_blank" rel="noopener noreferrer">
          <CreditCard className="h-4 w-4 mr-1" />
          Add Payment
        </a>
      </Button>
    </div>
  );
}
